import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { EmailMessageApiService } from '../services/email-message-api.service';
import { CorreoDetailModalComponent } from '../correo-detail-modal/correo-detail-modal.component';
import {
  EmailMessageSummaryResponse,
  MyEmailsSearchParams,
  EMAIL_STATUS_OPTIONS,
} from '../models/email-message.model';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

/**
 * Lista paginada de los correos que envió el usuario logueado.
 * Consume GET /api/emails/mine (businessId del JWT, no se envía manualmente).
 */
@Component({
  selector: 'app-correos-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, CorreoDetailModalComponent],
  templateUrl: './correos-list.component.html',
})
export class CorreosListComponent implements OnInit {
  correos: EmailMessageSummaryResponse[] = [];
  loading = false;
  error = '';

  // Modal de detalle
  showDetailModal = false;
  selectedEmailId: number | null = null;

  // Filtros
  showFilters = false;
  filtroStatus = '';
  filtroContactId: number | null = null;
  filtroFrom = ''; // yyyy-MM-dd (input date)
  filtroTo = '';   // yyyy-MM-dd

  readonly statusOptions = EMAIL_STATUS_OPTIONS;

  // Paginación (shape del contrato: page/size/totalElements/totalPages/hasNext/hasPrevious)
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  hasNext = false;
  hasPrevious = false;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'correos-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 20;

  constructor(
    private emailApi: EmailMessageApiService,
    private datetime: DatetimeService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarCorreos();
  }

  cargarCorreos(): void {
    this.loading = true;
    this.error = '';

    const params: MyEmailsSearchParams = {
      status: this.filtroStatus || undefined,
      idContact: this.filtroContactId ?? undefined,
      from: this.toOffsetStart(this.filtroFrom),
      to: this.toOffsetEnd(this.filtroTo),
      page: this.globalPage,
      size: this.pageSize,
    };

    this.emailApi.searchMyEmails(params).subscribe({
      next: (res) => {
        this.correos = res.content;
        this.globalPage = res.page ?? this.globalPage;
        this.globalTotalPages = res.totalPages;
        this.globalTotalElements = res.totalElements;
        this.hasNext = res.hasNext;
        this.hasPrevious = res.hasPrevious;
        this.loading = false;
      },
      error: (err) => {
        const msg = err.error?.message || 'Error al cargar los correos';
        this.error = msg;
        this.loading = false;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargarCorreos();
  }

  limpiarFiltros(): void {
    this.filtroStatus = '';
    this.filtroContactId = null;
    this.filtroFrom = '';
    this.filtroTo = '';
    this.globalPage = 0;
    this.cargarCorreos();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  /** Convierte un input date (yyyy-MM-dd) al inicio del día en ISO con offset local. */
  private toOffsetStart(date: string): string | undefined {
    if (!date) return undefined;
    return new Date(`${date}T00:00:00`).toISOString();
  }

  /** Convierte un input date (yyyy-MM-dd) al inicio del día siguiente (to es exclusivo). */
  private toOffsetEnd(date: string): string | undefined {
    if (!date) return undefined;
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  }

  // ── Detalle ──

  verDetalle(correo: EmailMessageSummaryResponse): void {
    this.selectedEmailId = correo.id;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedEmailId = null;
  }

  // ── Badge de estado ──

  statusColor(status: string): string {
    switch (status) {
      case 'SENT':
      case 'DELIVERED':
        return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
      case 'QUEUED':
      case 'SENDING':
      case 'DEFERRED':
        return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-orange-400';
      case 'FAILED':
      case 'BOUNCED':
        return 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-500';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-white/80';
    }
  }

  statusLabel(status: string): string {
    const found = this.statusOptions.find((o) => o.value === status);
    return found ? found.label : status;
  }

  formatDate(iso?: string | null): string {
    return this.datetime.formatDateTimeShort(iso);
  }

  // ── Paginación ──

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarCorreos();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pageDisplay(): number {
    return this.globalPage + 1;
  }
}
